import { computed, EventEmitter, inject, input, linkedSignal, signal, viewChild, } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { COARSE_METRIC_PERIODS_PER_YEAR, FREQUENCY_OPTIONS, FREQUENCY_PERIODS_PER_YEAR, FUND_METRIC_SEARCH_TYPE, HISTORICAL_CHART_DATA_MODE, LATEST_CHART_DATA_MODE, MAX_PERIODS, UNIVERSE_HIDDEN_CATEGORIES, UNIVERSE_QUARTILE_ZOOM_OPTIONS, UNIVERSE_SECURITY_GROUP_TABS, ZOOM_YEARS, } from '../../constants';
import { UniverseQuartileChartFrequency } from 'ChartApp/ts/features/universe-quartile-chart/constants';
import { ChartDateZoom } from 'ChartApp/ts/enums/chart-common.enum';
import { SecurityListMenuService } from 'SecurityListsApp/ts/services/security-list-menu.service';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../../../../ycharts/static/ts/features/directives/disabled/disabled.directive";
import * as i2 from "../../../fundamental-chart/components/fund-chart-date-zoom/fund-chart-date-zoom.component";
import * as i3 from "../../../../../../../../../ycharts/static/ts/features/directives/icon/icon.directive";
import * as i4 from "../../../../../../../../calculations/static/calculations/ts/features/metric-selector-modal/components/single-metric-selector-modal/single-metric-selector-modal.component";
import * as i5 from "../../../../../../../../security_lists/static/security_lists/ts/features/security-list-selector-modals/security-list-selector-modal/security-list-selector-modal.component";
import * as i6 from "../../../../../../../../../ycharts/static/ts/features/directives/popovers/tooltip/tooltip.directive";
import * as i7 from "../../../universe-quartile-chart/components/universe-quartile-chart/universe-quartile-chart.component";
const _c0 = ["securityListSelectorModal"];
const _c1 = ["metricSelectorModal"];
const _c2 = () => [];
const _c3 = () => ({ list: "chart-control-compact chart-control-responsive pl-40" });
function FundUniverseQuartileChartComponent_Conditional_5_For_3_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 34)(1, "div", 35);
    i0.ɵɵlistener("click", function FundUniverseQuartileChartComponent_Conditional_5_For_3_Template_div_click_1_listener() { const freq_r3 = i0.ɵɵrestoreView(_r2).$implicit; const ctx_r3 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r3.selectFrequency(freq_r3.value)); });
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const freq_r3 = ctx.$implicit;
    const $index_r5 = ctx.$index;
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵclassProp("active", freq_r3.value === ctx_r3.effectiveFrequency());
    i0.ɵɵproperty("disabled", freq_r3.disabled);
    i0.ɵɵattribute("data-tab-index", $index_r5);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", freq_r3.label, " ");
} }
function FundUniverseQuartileChartComponent_Conditional_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6)(1, "ul", 33);
    i0.ɵɵrepeaterCreate(2, FundUniverseQuartileChartComponent_Conditional_5_For_3_Template, 3, 5, "div", 34, i0.ɵɵrepeaterTrackByIndex);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵrepeater(ctx_r3.frequencyOptions());
} }
function FundUniverseQuartileChartComponent_Conditional_38_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ycn-fund-chart-date-zoom", 36);
    i0.ɵɵlistener("selectZoomOptionHandler", function FundUniverseQuartileChartComponent_Conditional_38_Template_ycn_fund_chart_date_zoom_selectZoomOptionHandler_0_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.selectZoom($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("zoom", ctx_r3.effectiveZoom())("zoomOptions", ctx_r3.zoomOptions())("customCssClasses", i0.ɵɵpureFunction0(3, _c3));
} }
/**
 *
 * Fund Universe Quartile Chart Component
 *
 * Wraps `UniverseQuartileChartComponent` with the controls for the parameters it charts: the
 * timeframe, how finely it is bucketed, the universe, the metric, and whether the benchmark is
 * overlaid.
 *
 * The chart itself owns no controls, which is what lets it be dropped into a report module or a
 * dashboard panel with fixed parameters. This component is the other half of that split: it holds
 * the parameters as state and hands them down as inputs.
 *
 *
 * Input Properties:
 *  targetSecurities: securities being compared against the universe; passed straight through
 *  metric / securitylistName / securityGroup / zoom / frequency / includeBenchmark: initial
 *      selections, re-seeding their controls whenever the input itself changes
 *  title: presentation only
 *
 * Output Properties:
 *  chartDataHandler: re-emitted from the chart on every settled load
 *  stateChangeHandler: the full parameter set, whenever a control moves it
 */
export class FundUniverseQuartileChartComponent {
    constructor() {
        this.securityListMenuService = inject(SecurityListMenuService);
        this.targetSecurities = input([], ...(ngDevMode ? [{ debugName: "targetSecurities" }] : []));
        this.metric = input('', ...(ngDevMode ? [{ debugName: "metric" }] : []));
        this.securitylistName = input('', ...(ngDevMode ? [{ debugName: "securitylistName" }] : []));
        this.securityGroup = input('', ...(ngDevMode ? [{ debugName: "securityGroup" }] : []));
        this.zoom = input(ChartDateZoom.CHART_ZOOM_ONE_YEAR, ...(ngDevMode ? [{ debugName: "zoom" }] : []));
        this.frequency = input(UniverseQuartileChartFrequency.MONTHLY, ...(ngDevMode ? [{ debugName: "frequency" }] : []));
        this.includeBenchmark = input(false, ...(ngDevMode ? [{ debugName: "includeBenchmark" }] : []));
        this.title = input(null, ...(ngDevMode ? [{ debugName: "title" }] : []));
        this.dataMode = signal(HISTORICAL_CHART_DATA_MODE, ...(ngDevMode ? [{ debugName: "dataMode" }] : []));
        this.chartDataHandler = new EventEmitter();
        this.stateChangeHandler = new EventEmitter();
        /** Live selections. `linkedSignal` re-seeds each one if its input is replaced. */
        this.selectedMetric = linkedSignal(() => this.metric(), ...(ngDevMode ? [{ debugName: "selectedMetric" }] : []));
        this.selectedSecuritylistName = linkedSignal(() => this.securitylistName(), ...(ngDevMode ? [{ debugName: "selectedSecuritylistName" }] : []));
        this.selectedSecurityGroup = linkedSignal(() => this.securityGroup(), ...(ngDevMode ? [{ debugName: "selectedSecurityGroup" }] : []));
        this.selectedZoom = linkedSignal(() => this.zoom(), ...(ngDevMode ? [{ debugName: "selectedZoom" }] : []));
        this.selectedFrequency = linkedSignal(() => this.frequency() ?? UniverseQuartileChartFrequency.MONTHLY, ...(ngDevMode ? [{ debugName: "selectedFrequency" }] : []));
        this.benchmarkIncluded = linkedSignal(() => this.includeBenchmark(), ...(ngDevMode ? [{ debugName: "benchmarkIncluded" }] : []));
        this.securityListSelectorModal = viewChild('securityListSelectorModal', ...(ngDevMode ? [{ debugName: "securityListSelectorModal" }] : []));
        this.metricSelectorModal = viewChild('metricSelectorModal', ...(ngDevMode ? [{ debugName: "metricSelectorModal" }] : []));
        /**
         * The metric the endpoint last reported on, which is what says whether a frequency applies
         * and which frequencies are coarse enough for it.
         */
        this.chartedMetric = signal(null, ...(ngDevMode ? [{ debugName: "chartedMetric" }] : []));
        this.zoomOptions = computed(() => {
            return UNIVERSE_QUARTILE_ZOOM_OPTIONS.map((zoom_option) => {
                const maxFrequencyAvailable = Math.max(1 / (COARSE_METRIC_PERIODS_PER_YEAR[this.chartedMetric()?.frequency ?? 'monthly'] || 12), 1 / (COARSE_METRIC_PERIODS_PER_YEAR[this.selectedFrequency() ?? 'monthly'] || 12));
                return {
                    ...zoom_option,
                    enabled: ZOOM_YEARS[zoom_option.value] >= maxFrequencyAvailable,
                };
            });
        }, ...(ngDevMode ? [{ debugName: "zoomOptions" }] : []));
        /**
         * The timeframe actually charted.
         *
         * If the new metric doesn't support the current timeframe, temporary fallback to the shortest available option.
         * We don't save this temporary change to selectedZoom, preserving the user's original selection for finer metrics.
         */
        this.effectiveZoom = computed(() => {
            const options = this.zoomOptions();
            const selected = this.selectedZoom();
            if (options.some((option) => option.enabled && option.value === selected))
                return selected;
            // Ordered shortest first, so the first still offered is the closest to what was asked for.
            return options.find((option) => option.enabled)?.value ?? selected;
        }, ...(ngDevMode ? [{ debugName: "effectiveZoom" }] : []));
        this.securityGroupTabs = UNIVERSE_SECURITY_GROUP_TABS;
        this.hiddenUniverseCategories = UNIVERSE_HIDDEN_CATEGORIES;
        /** Either chart picks from the same menu: every fund metric, with or without a timeline. */
        this.metricSearchType = FUND_METRIC_SEARCH_TYPE;
        this.isHistoricalMode = computed(() => this.dataMode() === HISTORICAL_CHART_DATA_MODE, ...(ngDevMode ? [{ debugName: "isHistoricalMode" }] : []));
        this.isLatestMode = computed(() => this.dataMode() === LATEST_CHART_DATA_MODE, ...(ngDevMode ? [{ debugName: "isLatestMode" }] : []));
        /**
         * Every list's display name, keyed by group and then by the key the endpoint names it with.
         *
         */
        this.securityListLabels = toSignal(this.securityListMenuService.getLabels().pipe(catchError(() => of(null))), {
            initialValue: null,
        });
        /** The universe as the user should read it, falling back to its key until the labels land. */
        this.universeLabel = computed(() => {
            const securitylistName = this.selectedSecuritylistName();
            if (!securitylistName)
                return '';
            const group = this.selectedSecurityGroup();
            return this.securityListLabels()?.[group]?.[securitylistName] || securitylistName;
        }, ...(ngDevMode ? [{ debugName: "universeLabel" }] : []));
        /**
         * The metric as the user reads it.
         *
         * The chart payload names it, so the control needs no lookup of its own: before the first
         * load the metric's own id stands in, which is also what shows for one that does not resolve.
         */
        this.metricLabel = computed(() => {
            const charted = this.chartedMetric();
            return charted?.name === this.selectedMetric() ? charted.label : this.selectedMetric();
        }, ...(ngDevMode ? [{ debugName: "metricLabel" }] : []));
        /** Reopening the modal should land on the universe already charted. */
        this.submittedSecurityList = computed(() => {
            const securitylistName = this.selectedSecuritylistName();
            return securitylistName ? { key: securitylistName } : null;
        }, ...(ngDevMode ? [{ debugName: "submittedSecurityList" }] : []));
        /**
         * A metric with no history is a single distribution, not a series, so there is no timeline to
         * bucket and the control has nothing to offer.
         */
        this.showFrequency = computed(() => this.isHistoricalMode() && this.chartedMetric()?.hasHistory !== false, ...(ngDevMode ? [{ debugName: "showFrequency" }] : []));
        /**
         * Frequencies the endpoint will accept for the current zoom and metric, the rest shown
         * disabled so it is visible that they exist but do not apply here.
         */
        this.frequencyOptions = computed(() => FREQUENCY_OPTIONS.map((option) => ({
            ...option,
            disabled: !this.isFrequencyAccepted(option.value),
        })), ...(ngDevMode ? [{ debugName: "frequencyOptions" }] : []));
        /**
         * The frequency actually charted.
         *
         * The zoom is authoritative: picking a timeframe never fails, and the frequency gives way when
         * the pair would be rejected. Snapping here rather than writing back to `selectedFrequency`
         * keeps the user's choice intact for when it becomes valid again -- and keeps the correction
         * one-directional, so the two controls cannot chase each other.
         */
        this.effectiveFrequency = computed(() => {
            // A latest chart buckets nothing, which is what the endpoint makes of a metric with no
            // history anyway.
            if (this.isLatestMode())
                return UniverseQuartileChartFrequency.NONE;
            const selected = this.selectedFrequency();
            if (selected !== this.chartedMetric()?.frequency && this.isFrequencyAccepted(selected))
                return selected;
            const fallback = this.frequencyOptions().find((option) => !option.disabled);
            return fallback?.value ?? UniverseQuartileChartFrequency.NONE;
        }, ...(ngDevMode ? [{ debugName: "effectiveFrequency" }] : []));
        this.effectiveTitle = computed(() => {
            const inputTitle = this.title();
            return inputTitle ?? `${this.chartedMetric()?.label ?? this.metric()} + Universe`;
        }, ...(ngDevMode ? [{ debugName: "effectiveTitle" }] : []));
        /** The current selection, in the shape a host would save. */
        this.chartState = computed(() => ({
            targetSecurities: this.targetSecurities(),
            metric: this.selectedMetric(),
            securitylistName: this.selectedSecuritylistName(),
            securityGroup: this.selectedSecurityGroup(),
            zoom: this.effectiveZoom(),
            frequency: this.effectiveFrequency(),
            includeBenchmark: this.benchmarkIncluded(),
        }), ...(ngDevMode ? [{ debugName: "chartState" }] : []));
    }
    /**
     * Whether the endpoint would accept this frequency, by the two rules `_validate_frequency`
     * applies: the pair has to produce between one and `MAX_PERIODS` boxes, and the chart cannot
     * be bucketed more finely than the metric reports.
     */
    isFrequencyAccepted(frequency) {
        const periodsPerYear = FREQUENCY_PERIODS_PER_YEAR[frequency];
        if (!periodsPerYear)
            return false;
        // An unrecognized zoom spans nothing, which fails the period check below rather than
        // letting a NaN comparison fall through as acceptable.
        const years = ZOOM_YEARS[this.effectiveZoom()] ?? 0;
        const totalPeriods = periodsPerYear * years;
        if (totalPeriods < 1 || totalPeriods > MAX_PERIODS)
            return false;
        const metricFrequency = this.chartedMetric()?.frequency;
        const metricPeriodsPerYear = metricFrequency ? COARSE_METRIC_PERIODS_PER_YEAR[metricFrequency] : undefined;
        return metricPeriodsPerYear === undefined || periodsPerYear <= metricPeriodsPerYear;
    }
    openUniverseModal() {
        this.securityListSelectorModal()?.openModal();
    }
    openMetricModal() {
        this.metricSelectorModal()?.openModal();
    }
    selectZoom(zoomOption) {
        this.selectedZoom.set(zoomOption.value);
    }
    selectFrequency(frequency) {
        this.selectedFrequency.set(frequency);
    }
    selectMetric(metric) {
        this.chartMetric(metric.internal_name);
    }
    /**
     * The modal carries its own search, which reports an `AutocompleteResult` rather than a
     * `Metric`. Its `search_id` is the same identifier `internal_name` is.
     */
    selectMetricFromSearch(result) {
        this.chartMetric(result.search_id);
    }
    /**
     * Chart the metric, whichever chart this is.
     *
     * Either kind of metric can be picked here: the settled load reports whether the one charted
     * has a history, and `handleChartData` puts the chart into the mode that suits it.
     */
    chartMetric(internalName) {
        // Optimistic dta reset. We assume that the selected metric will be historical and let the code correct
        // the entry later. Else it gets stuck when latest calcs are selected
        if (internalName === this.selectedMetric())
            return;
        this.dataMode.set(HISTORICAL_CHART_DATA_MODE);
        this.selectedMetric.set(internalName);
    }
    /**
     * The modal reports a list by its key, which is what the endpoint names a universe by. The set
     * operation it also emits is meaningless for a single universe and the modal is configured not
     * to offer it.
     */
    selectUniverse(submission) {
        this.selectedSecuritylistName.set(submission.securityList.key ?? '');
        this.selectedSecurityGroup.set(submission.securityGroup);
    }
    toggleBenchmark(included) {
        this.benchmarkIncluded.set(included);
    }
    handleChartData(event) {
        // Update metric only if new metric is received. This in order to prevent the metric to stop showing the metric label
        // in case of an error
        if (event.metric && event.metric !== this.chartedMetric()) {
            this.chartedMetric.set(event.metric);
            this.dataMode.set(event.metric.hasHistory ? HISTORICAL_CHART_DATA_MODE : LATEST_CHART_DATA_MODE);
        }
        if (!event.errorMessage.length) {
            this.stateChangeHandler.emit(this.chartState());
        }
        this.chartDataHandler.emit(event);
    }
}
FundUniverseQuartileChartComponent.ɵfac = function FundUniverseQuartileChartComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundUniverseQuartileChartComponent)(); };
FundUniverseQuartileChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundUniverseQuartileChartComponent, selectors: [["ycn-fund-universe-quartile-chart"]], viewQuery: function FundUniverseQuartileChartComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuerySignal(ctx.securityListSelectorModal, _c0, 5);
        i0.ɵɵviewQuerySignal(ctx.metricSelectorModal, _c1, 5);
    } if (rf & 2) {
        i0.ɵɵqueryAdvance(2);
    } }, inputs: { targetSecurities: [1, "targetSecurities"], metric: [1, "metric"], securitylistName: [1, "securitylistName"], securityGroup: [1, "securityGroup"], zoom: [1, "zoom"], frequency: [1, "frequency"], includeBenchmark: [1, "includeBenchmark"], title: [1, "title"] }, outputs: { chartDataHandler: "chartDataHandler", stateChangeHandler: "stateChangeHandler" }, standalone: false, decls: 44, vars: 29, consts: [["metricSelectorModal", ""], ["securityListSelectorModal", ""], [1, "panel", "panel-data"], [1, "panel-header"], ["data-test-id", "quartile-chart-title", 1, "panel-title"], [1, "panel-actions"], [1, "panel-action"], [1, "panel-action", "mr-1"], ["data-test-id", "quartile-chart-benchmark", 1, "custom-control", "custom-checkbox", "form-label", "form-label-thin", "ml-1"], ["type", "checkbox", 1, "custom-control-input", 3, "change", "checked"], [1, "custom-control-indicator"], [1, "custom-control-description"], [1, "panel-content"], [1, "chart-options"], ["data-test-id", "quartile-chart-selectors", 1, "row"], [1, "col-6", "d-flex", "align-items-baseline"], [1, "bullet", "bullet-1"], [1, "form-label", "form-label-thin", "mr-1"], [1, "form-group-editable"], [1, "form-value"], ["data-test-id", "quartile-chart-metric-value"], ["data-test-id", "quartile-chart-metric-edit", "ycnTooltip", "", 1, "btn", "btn-circle", "btn-transparent", "btn-edit", 3, "click", "tooltipTitle"], ["ycnIcon", "", 3, "name"], [1, "bullet-range"], ["data-test-id", "quartile-chart-universe-value"], ["data-test-id", "quartile-chart-universe-edit", "ycnTooltip", "", 1, "btn", "btn-circle", "btn-transparent", "btn-edit", 3, "click", "tooltipTitle"], [1, "chart-cover"], [1, "chart-interactive"], [1, "chart-item"], ["data-test-id", "quartile-chart-zoom", 1, "col-8", "d-flex", "align-items-baseline", "mb-2", 3, "zoom", "zoomOptions", "customCssClasses"], [3, "chartDataHandler", "targetSecurities", "metric", "securitylistName", "securityGroup", "zoom", "frequency", "includeBenchmark"], [3, "submitModalHandler", "submitModalAutocompleteHandler", "searchType", "categories", "fetchCategories", "selectedMetricName"], [3, "submitHandler", "activeSecurityGroupTab", "headerText", "hiddenCategories", "securityGroupTabOptions", "showSetOperations", "submittedSecurityList", "submittedSecurityListSecurityGroup"], ["data-test-id", "quartile-chart-frequency", 1, "nav", "nav-tabs"], [1, "nav-item"], ["ycnDisabled", "", 1, "nav-link", 3, "click", "disabled"], ["data-test-id", "quartile-chart-zoom", 1, "col-8", "d-flex", "align-items-baseline", "mb-2", 3, "selectZoomOptionHandler", "zoom", "zoomOptions", "customCssClasses"]], template: function FundUniverseQuartileChartComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelementStart(0, "div", 2)(1, "div", 3)(2, "h3", 4);
        i0.ɵɵtext(3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "div", 5);
        i0.ɵɵconditionalCreate(5, FundUniverseQuartileChartComponent_Conditional_5_Template, 4, 0, "div", 6);
        i0.ɵɵelementStart(6, "div", 7)(7, "label", 8)(8, "input", 9);
        i0.ɵɵlistener("change", function FundUniverseQuartileChartComponent_Template_input_change_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.toggleBenchmark($event.target.checked)); });
        i0.ɵɵelementEnd();
        i0.ɵɵelement(9, "span", 10);
        i0.ɵɵelementStart(10, "span", 11);
        i0.ɵɵtext(11, "Include Benchmark");
        i0.ɵɵelementEnd()()()()();
        i0.ɵɵelementStart(12, "div", 12)(13, "div", 13)(14, "div", 14)(15, "div", 15);
        i0.ɵɵelement(16, "span", 16);
        i0.ɵɵelementStart(17, "label", 17);
        i0.ɵɵtext(18, "Metric");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(19, "div", 18)(20, "div", 19)(21, "div", 20);
        i0.ɵɵtext(22);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(23, "div", 21);
        i0.ɵɵlistener("click", function FundUniverseQuartileChartComponent_Template_div_click_23_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.openMetricModal()); });
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelement(24, "svg", 22);
        i0.ɵɵelementEnd()()()();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(25, "div", 15);
        i0.ɵɵelement(26, "span", 23);
        i0.ɵɵelementStart(27, "label", 17);
        i0.ɵɵtext(28, "Universe");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(29, "div", 18)(30, "div", 19)(31, "div", 24);
        i0.ɵɵtext(32);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(33, "div", 25);
        i0.ɵɵlistener("click", function FundUniverseQuartileChartComponent_Template_div_click_33_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.openUniverseModal()); });
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelement(34, "svg", 22);
        i0.ɵɵelementEnd()()()()()();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(35, "div", 26)(36, "div", 27)(37, "div", 28);
        i0.ɵɵconditionalCreate(38, FundUniverseQuartileChartComponent_Conditional_38_Template, 1, 4, "ycn-fund-chart-date-zoom", 29);
        i0.ɵɵelementStart(39, "ycn-universe-quartile-chart", 30);
        i0.ɵɵlistener("chartDataHandler", function FundUniverseQuartileChartComponent_Template_ycn_universe_quartile_chart_chartDataHandler_39_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleChartData($event)); });
        i0.ɵɵelementEnd()()()()()();
        i0.ɵɵelementStart(40, "ycn-single-metric-selector-modal", 31, 0);
        i0.ɵɵlistener("submitModalHandler", function FundUniverseQuartileChartComponent_Template_ycn_single_metric_selector_modal_submitModalHandler_40_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.selectMetric($event)); })("submitModalAutocompleteHandler", function FundUniverseQuartileChartComponent_Template_ycn_single_metric_selector_modal_submitModalAutocompleteHandler_40_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.selectMetricFromSearch($event)); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(42, "ycn-security-list-selector-modal", 32, 1);
        i0.ɵɵlistener("submitHandler", function FundUniverseQuartileChartComponent_Template_ycn_security_list_selector_modal_submitHandler_42_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.selectUniverse($event)); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate(ctx.effectiveTitle());
        i0.ɵɵadvance(2);
        i0.ɵɵconditional(ctx.showFrequency() ? 5 : -1);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("checked", ctx.benchmarkIncluded());
        i0.ɵɵadvance(14);
        i0.ɵɵtextInterpolate(ctx.metricLabel());
        i0.ɵɵadvance();
        i0.ɵɵproperty("tooltipTitle", "Select a metric");
        i0.ɵɵadvance();
        i0.ɵɵproperty("name", "icon-edit");
        i0.ɵɵadvance(8);
        i0.ɵɵtextInterpolate(ctx.universeLabel());
        i0.ɵɵadvance();
        i0.ɵɵproperty("tooltipTitle", "Select a universe");
        i0.ɵɵadvance();
        i0.ɵɵproperty("name", "icon-edit");
        i0.ɵɵadvance(4);
        i0.ɵɵconditional(ctx.isHistoricalMode() ? 38 : -1);
        i0.ɵɵadvance();
        i0.ɵɵproperty("targetSecurities", ctx.targetSecurities())("metric", ctx.selectedMetric())("securitylistName", ctx.selectedSecuritylistName())("securityGroup", ctx.selectedSecurityGroup())("zoom", ctx.effectiveZoom())("frequency", ctx.effectiveFrequency())("includeBenchmark", ctx.benchmarkIncluded());
        i0.ɵɵadvance();
        i0.ɵɵproperty("searchType", ctx.metricSearchType)("categories", i0.ɵɵpureFunction0(28, _c2))("fetchCategories", true)("selectedMetricName", ctx.selectedMetric());
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("activeSecurityGroupTab", "multi")("headerText", "Select a Universe")("hiddenCategories", ctx.hiddenUniverseCategories)("securityGroupTabOptions", ctx.securityGroupTabs)("showSetOperations", false)("submittedSecurityList", ctx.submittedSecurityList())("submittedSecurityListSecurityGroup", ctx.selectedSecurityGroup() || null);
    } }, dependencies: [i1.DisabledDirective, i2.FundChartDateZoomComponent, i3.IconDirective, i4.SingleMetricSelectorModalComponent, i5.SecurityListSelectorModalComponent, i6.TooltipDirective, i7.UniverseQuartileChartComponent], encapsulation: 2 });
